import { MutationHookOptions, QueryHookOptions, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  GQLQueryPlatformSaleCatalog_GetPlatformSaleCatalogsArgs,
  GQLPlatformSaleCatalogConnection,
  GQLQueryPlatformSaleCatalog_GetPlatformSaleCatalogWithPlatformMerchantContractsArgs,
  GQLPlatformSaleCatalogWithPlatformMerchantContractConnection,
  GQLPlatformSaleCatalogWithPlatformMerchantContract,
  GQLPlatformSaleCatalog,
  GQLQueryPlatformSaleCatalog_ExportPlatformPiecePricingCsvFromCatalogArgs,
  GQLQueryPlatformSaleCatalog_ExportPlatformAccessoryPricingCsvFromCatalogArgs,
  GQLMutationPlatformSaleCatalog_CreateFromPlatformSaleCatalogArgs,
  GQLMutationPlatformSaleCatalog_CreateFromDeliveryUnitSaleCatalogArgs,
  GQLMutationPlatformSaleCatalog_PromotePlatformSaleCatalogToMerchantActiveContractArgs,
  GQLMutationPlatformSaleCatalog_PublishPlatformSaleCatalogArgs,
  GQLMutationPlatformSaleCatalog_ImportPlatformPiecePricingCsvIntoCatalogArgs,
  GQLMutationPlatformSaleCatalog_ImportPlatformAccessoryPricingCsvIntoCatalogArgs,
  GQLMutationPlatformSaleCatalog_DeletePlatformSaleCatalogArgs,
  GQLMutationPlatformSaleCatalog_UpdateNameAndDescriptionArgs
} from '@jewelutions/core-crm-server-api-client-domain';
import { Exception } from '@jewelutions/core-common-domain';
import gql from 'graphql-tag';
import {
  PLATFORM_SALE_CATALOG_FIELDS,
  PLATFORM_SALE_CATALOG_WITH_PLATFORM_MERCHANT_CONTRACT_FIELDS
} from './fragments/platform';
import { OperationsHelper } from './OperationHelper';

export class PlatformSaleCatalogOperation {
  platformSaleCatalogArrayFromResponse(data: any): GQLPlatformSaleCatalog[] {
    return OperationsHelper.retrievePropertyFromGQLResponse(data, 'platformSaleCatalogs');
  }

  platformSaleCatalogWithPlatformMerchantContractArrayFromResponse(
    data: any
  ): GQLPlatformSaleCatalogWithPlatformMerchantContract[] {
    return OperationsHelper.retrievePropertyFromGQLResponse(
      data,
      'platformSaleCatalogWithPlatformMerchantContracts'
    );
  }

  platformSaleCatalogIdFromResponse(data: any): string {
    if (data.PlatformSaleCatalog_createFromPlatformSaleCatalog !== undefined) {
      return data.PlatformSaleCatalog_createFromPlatformSaleCatalog;
    }
    throw new Exception(`Cannot handle input data platformSaleCatalogIdFromResponse`);
  }

  getPlatformSaleCatalogs(
    args: GQLQueryPlatformSaleCatalog_GetPlatformSaleCatalogsArgs,
    options: QueryHookOptions<
      GQLPlatformSaleCatalogConnection,
      GQLQueryPlatformSaleCatalog_GetPlatformSaleCatalogsArgs
    > = {}
  ) {
    const query = gql`
      ${PLATFORM_SALE_CATALOG_FIELDS}
      query getPlatformSaleCatalogs($includeOnlyActiveContracts: Boolean!) {
        PlatformSaleCatalog_getPlatformSaleCatalogs(includeOnlyActiveContracts: $includeOnlyActiveContracts) {
          cursor
          hasMore
          platformSaleCatalogs {
            ...PlatformSaleCatalogFields
          }
        }
      }
    `;
    return useQuery<
      GQLPlatformSaleCatalogConnection,
      GQLQueryPlatformSaleCatalog_GetPlatformSaleCatalogsArgs
    >(query, { ...options, variables: args });
  }

  getPlatformSaleCatalogWithPlatformMerchantContracts(
    args: GQLQueryPlatformSaleCatalog_GetPlatformSaleCatalogWithPlatformMerchantContractsArgs,
    options: QueryHookOptions<
      GQLPlatformSaleCatalogWithPlatformMerchantContractConnection,
      GQLQueryPlatformSaleCatalog_GetPlatformSaleCatalogWithPlatformMerchantContractsArgs
    > = {}
  ) {
    const query = gql`
      ${PLATFORM_SALE_CATALOG_WITH_PLATFORM_MERCHANT_CONTRACT_FIELDS}
      query getPlatformSaleCatalogWithPlatformMerchantContracts($includeOnlyActiveContracts: Boolean!) {
        PlatformSaleCatalog_getPlatformSaleCatalogWithPlatformMerchantContracts(
          includeOnlyActiveContracts: $includeOnlyActiveContracts
        ) {
          cursor
          hasMore
          platformSaleCatalogWithPlatformMerchantContracts {
            ...PlatformSaleCatalogWithPlatformMerchantContractFields
          }
        }
      }
    `;
    return useQuery<
      GQLPlatformSaleCatalogWithPlatformMerchantContractConnection,
      GQLQueryPlatformSaleCatalog_GetPlatformSaleCatalogWithPlatformMerchantContractsArgs
    >(query, { ...options, variables: args });
  }

  exportPlatformPiecePricingCsvFromCatalogLazy(
    options: QueryHookOptions<
      String,
      GQLQueryPlatformSaleCatalog_ExportPlatformPiecePricingCsvFromCatalogArgs
    > = {}
  ) {
    const query = gql`
      query exportPlatformPiecePricingCsvFromCatalog($platformSaleCatalogId: ID!, $currency: Currency!) {
        PlatformSaleCatalog_exportPlatformPiecePricingCsvFromCatalog(
          platformSaleCatalogId: $platformSaleCatalogId
          currency: $currency
        )
      }
    `;
    return useLazyQuery<String, GQLQueryPlatformSaleCatalog_ExportPlatformPiecePricingCsvFromCatalogArgs>(
      query,
      {
        ...options
      }
    );
  }

  exportPlatformAccessoryPricingCsvFromCatalogLazy(
    options: QueryHookOptions<
      String,
      GQLQueryPlatformSaleCatalog_ExportPlatformAccessoryPricingCsvFromCatalogArgs
    > = {}
  ) {
    const query = gql`
      query exportPlatformAccessoryPricingCsvFromCatalog($platformSaleCatalogId: ID!, $currency: Currency!) {
        PlatformSaleCatalog_exportPlatformAccessoryPricingCsvFromCatalog(
          platformSaleCatalogId: $platformSaleCatalogId
          currency: $currency
        )
      }
    `;
    return useLazyQuery<String, GQLQueryPlatformSaleCatalog_ExportPlatformAccessoryPricingCsvFromCatalogArgs>(
      query,
      {
        ...options
      }
    );
  }

  updateNameAndDescription(
    options: MutationHookOptions<Boolean, GQLMutationPlatformSaleCatalog_UpdateNameAndDescriptionArgs> = {}
  ) {
    const mutation = gql`
      mutation PlatformSaleCatalog_updateNameAndDescription(
        $platformSaleCatalogId: ID!
        $name: TranslatedStringInput!
        $description: TranslatedStringInput!
      ) {
        PlatformSaleCatalog_updateNameAndDescription(
          platformSaleCatalogId: $platformSaleCatalogId
          name: $name
          description: $description
        )
      }
    `;
    return useMutation<Boolean, GQLMutationPlatformSaleCatalog_UpdateNameAndDescriptionArgs>(
      mutation,
      options
    );
  }

  createFromPlatformSaleCatalog(
    options: MutationHookOptions<
      String,
      GQLMutationPlatformSaleCatalog_CreateFromPlatformSaleCatalogArgs
    > = {}
  ) {
    const mutation = gql`
      mutation PlatformSaleCatalog_createFromPlatformSaleCatalog(
        $name: TranslatedStringInput!
        $description: TranslatedStringInput!
        $platformSaleCatalogId: ID!
      ) {
        PlatformSaleCatalog_createFromPlatformSaleCatalog(
          name: $name
          description: $description
          platformSaleCatalogId: $platformSaleCatalogId
        )
      }
    `;
    return useMutation<String, GQLMutationPlatformSaleCatalog_CreateFromPlatformSaleCatalogArgs>(
      mutation,
      options
    );
  }

  createFromDeliveryUnitSaleCatalog(
    options: MutationHookOptions<
      String,
      GQLMutationPlatformSaleCatalog_CreateFromDeliveryUnitSaleCatalogArgs
    > = {}
  ) {
    const mutation = gql`
      mutation PlatformSaleCatalog_createFromDeliveryUnitSaleCatalog(
        $input: PlatformSaleCatalogCreateFromDeliveryUnitSaleCatalogInput!
      ) {
        PlatformSaleCatalog_createFromDeliveryUnitSaleCatalog(input: $input)
      }
    `;
    return useMutation<String, GQLMutationPlatformSaleCatalog_CreateFromDeliveryUnitSaleCatalogArgs>(
      mutation,
      options
    );
  }

  promotePlatformSaleCatalogToMerchantActiveContract(
    options: MutationHookOptions<
      String,
      GQLMutationPlatformSaleCatalog_PromotePlatformSaleCatalogToMerchantActiveContractArgs
    > = {}
  ) {
    const mutation = gql`
      mutation PlatformSaleCatalog_promotePlatformSaleCatalogToMerchantActiveContract(
        $merchantPartyId: ID!
        $platformSaleCatalogId: ID!
      ) {
        PlatformSaleCatalog_promotePlatformSaleCatalogToMerchantActiveContract(
          merchantPartyId: $merchantPartyId
          platformSaleCatalogId: $platformSaleCatalogId
        )
      }
    `;
    return useMutation<
      String,
      GQLMutationPlatformSaleCatalog_PromotePlatformSaleCatalogToMerchantActiveContractArgs
    >(mutation, options);
  }

  publishPlatformSaleCatalog(
    options: MutationHookOptions<Boolean, GQLMutationPlatformSaleCatalog_PublishPlatformSaleCatalogArgs> = {}
  ) {
    const mutation = gql`
      mutation PlatformSaleCatalog_publishPlatformSaleCatalog($platformSaleCatalogId: ID!) {
        PlatformSaleCatalog_publishPlatformSaleCatalog(platformSaleCatalogId: $platformSaleCatalogId)
      }
    `;
    return useMutation<Boolean, GQLMutationPlatformSaleCatalog_PublishPlatformSaleCatalogArgs>(
      mutation,
      options
    );
  }

  deletePlatformSaleCatalog(
    options: MutationHookOptions<Boolean, GQLMutationPlatformSaleCatalog_DeletePlatformSaleCatalogArgs> = {}
  ) {
    const mutation = gql`
      mutation PlatformSaleCatalog_deletePlatformSaleCatalog($platformSaleCatalogId: ID!) {
        PlatformSaleCatalog_deletePlatformSaleCatalog(platformSaleCatalogId: $platformSaleCatalogId)
      }
    `;
    return useMutation<Boolean, GQLMutationPlatformSaleCatalog_DeletePlatformSaleCatalogArgs>(
      mutation,
      options
    );
  }

  importPlatformPiecePricingCsvIntoCatalog(
    options: MutationHookOptions<
      Boolean,
      GQLMutationPlatformSaleCatalog_ImportPlatformPiecePricingCsvIntoCatalogArgs
    > = {}
  ) {
    const mutation = gql`
      mutation PlatformSaleCatalog_importPlatformPiecePricingCsvIntoCatalog(
        $platformSaleCatalogId: ID!
        $csv: Upload!
      ) {
        PlatformSaleCatalog_importPlatformPiecePricingCsvIntoCatalog(
          platformSaleCatalogId: $platformSaleCatalogId
          csv: $csv
        )
      }
    `;
    return useMutation<Boolean, GQLMutationPlatformSaleCatalog_ImportPlatformPiecePricingCsvIntoCatalogArgs>(
      mutation,
      options
    );
  }

  importPlatformAccessoryPricingCsvIntoCatalog(
    options: MutationHookOptions<
      Boolean,
      GQLMutationPlatformSaleCatalog_ImportPlatformAccessoryPricingCsvIntoCatalogArgs
    > = {}
  ) {
    const mutation = gql`
      mutation PlatformSaleCatalog_importPlatformAccessoryPricingCsvIntoCatalog(
        $platformSaleCatalogId: ID!
        $csv: Upload!
      ) {
        PlatformSaleCatalog_importPlatformAccessoryPricingCsvIntoCatalog(
          platformSaleCatalogId: $platformSaleCatalogId
          csv: $csv
        )
      }
    `;
    return useMutation<
      Boolean,
      GQLMutationPlatformSaleCatalog_ImportPlatformAccessoryPricingCsvIntoCatalogArgs
    >(mutation, options);
  }
}
